import BaseDecorator from "../modules/base/base-decorator";
import { Injector } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { BaseService } from "src/modules/base/shared/services/base.service";

// #201086 — extra payment fields per payment mode (card / cheque / NEFT / UPI details).
// - Shown only when facility config payment / payment_mode101 is "Yes"; otherwise the form fields stay hidden
//   whatever mode is picked, and their grid columns are hidden.
// - Mandatory per field comes from the payment mode master: the payment JSONs copy its *_mandatory flags into
//   hidden fields via populate_field, and those hidden fields' JSON dependents set required. The composite
//   edit-row dialog skips populate_field unless the mode was picked by hand, so the ticked flags are re-pushed here.
// Use it directly as the payment composite's decorator, or from a decorator the screen already has:
// loadConfig() on init, applyFlags() in valueChangeEnd, hideGridColumns() once the payment grid has rows.
export default class PaymentModeFlagsDecorator extends BaseDecorator {
    static readonly EXTRA_FIELDS = ['card_name', 'card_holder_name', 'cheque_date', 'bank_branch_name', 'drawee_bank_code', 'drawee_bank_name', 'drawee_name', 'slip_no',
        'locking_period_days', 'cheque_account_no', 'utr_date', 'individual_party', 'utr_no', 'account_no', 'payee_name', 'upi_id'];
    static extraFieldsEnabled = false;

    constructor(
        public route: ActivatedRoute,
        public name = 'PaymentModeFlagsDecorator',
        public injector: Injector
    ) {
        super(route, 'PaymentModeFlagsDecorator', injector)
        if (injector != undefined && injector.get != undefined) {
            PaymentModeFlagsDecorator.loadConfig(injector.get(BaseService));
        }
    }
    valueChangeEnd(e: any, obj: any = false) {
        PaymentModeFlagsDecorator.applyFlags(e, obj);
    }

    static loadConfig(baseService: BaseService, done?: () => void) {
        baseService.get('GET_CONFIG', 'module_code:payment,config_code:payment_mode101', 'facility_config_mapping', 'facility-management').subscribe(
            result => {
                PaymentModeFlagsDecorator.extraFieldsEnabled = !!(result && result.errCode == 0 && result.resource && result.resource.length > 0
                    && String(result.resource[0].config_value).toLowerCase() == 'yes');
                if (done) done();
            });
    }

    static applyFlags(e: any, obj: any) {
        const question = e?.question;
        const controls = obj?.form?.controls;
        if (!question || !controls || question.datasource?.query_id != 'GET_PAYMENT_MODE') return;
        const getterKey = question.getterKey || 'code';
        const selected = (question.options || []).find(option => option && option[getterKey] == controls[question.key]?.value);
        PaymentModeFlagsDecorator.fillDraweeBank(selected, controls);
        if (!PaymentModeFlagsDecorator.extraFieldsEnabled) {
            // the JSON `visible` dependents have already run for the picked mode; undo them the way base hides a field
            PaymentModeFlagsDecorator.EXTRA_FIELDS.filter(key => controls[key]).forEach(key => {
                controls[key].disable();
                controls[key].setValidators(null);
                if (controls[key]['question_component']?.question) controls[key]['question_component'].question.visible = false;
            });
            return;
        }
        if (!selected) return;
        Object.keys(controls).filter(key => key.endsWith('_mandatory') && selected[key] && controls[key]['question_component']).forEach(key => {
            controls[key].setValue(true);
            controls[key]['question_component'].updateDependentValue(true);
        });
    }

    // Cheque: pre-select the master's default drawee bank in Drawee Bank Name (drawee_bank_code, same bank_name valueset as
    // the master) when it is still empty. The searchselect keeps the written code and selects it once its options load;
    // drawee_bank_name is filled here as the drawee_bank_code -> drawee_bank_name populate only fires on a manual pick.
    static fillDraweeBank(selected: any, controls: any) {
        if (!selected || selected.code != 'cheque' || !selected.default_drawee_bank_code || !controls.drawee_bank_code || controls.drawee_bank_code.value) return;
        controls.drawee_bank_code.setValue(selected.default_drawee_bank_code);
        if (controls.drawee_bank_name) controls.drawee_bank_name.setValue(selected.default_drawee_bank_name || '');
    }

    static hideGridColumns(compositeArray: any) {
        if (!compositeArray || !Array.isArray(compositeArray.fields)) return;
        compositeArray.fields.filter(field => field && PaymentModeFlagsDecorator.EXTRA_FIELDS.includes(field.name))
            .forEach(field => field.hidden = !PaymentModeFlagsDecorator.extraFieldsEnabled);
    }
}
